import { __awaiter } from "tslib";
import React, { useState, useEffect, useMemo } from 'react';
import { useLazyQuery } from '@apollo/react-hooks';
import { useParams } from 'react-router-dom';
import { fetchOverlayQuery } from '@ligr/shared/lib/Overlay/gql/queries/overlay';
import { OverlayContainer } from '../lib/overlayContainer';
import { Auth } from '../lib/authContainer';
import { DelayedSummary, DelayedSummaryWithClientSkewCorrection } from '../lib/delayedSummaryContainer';
import { OverlayHeartbeat } from '@ligr/shared';
import { ClockContainer } from '../lib/clockContainer';
import { Automation } from '../lib/automation/automationContainer';
import { GraphicWrapper } from '../components/GraphicWrapper';
import FontFaceObserver from 'fontfaceobserver';
import { Ads } from '../lib/adsContainer';
import { dimensionsFromResolution } from '../utils/dimensionsFromResolution';
import { detachedAd } from '../components/DetatchedAd';
import { Background, MaxAreaWatermark, useNoLagSmoothingWhenHidden } from '@ligr/themes';
import { DesiredGfxState } from '../lib/useGfxState';
import { Manual } from '../lib/manualContainer';
import camelCase from 'camelcase';
export const OverlayComponent = ({ resizable, filteredGraphics, noAnimation }) => {
    var _a, _b, _c, _d, _e;
    const [theme, setTheme] = useState();
    const [themeFontsLoaded, setThemeFontsLoaded] = useState(false);
    useNoLagSmoothingWhenHidden();
    const { overlay, match } = OverlayContainer.useContainer();
    const loadTheme = (sport, themeName = 'default') => __awaiter(void 0, void 0, void 0, function* () {
        const { loadTheme } = (yield import(`@ligr/themes/lib/${camelCase(sport)}/index.js`)).default;
        const theme = (yield loadTheme(themeName)).default;
        setTheme(theme);
    });
    useEffect(() => {
        var _a;
        if ((_a = match === null || match === void 0 ? void 0 : match.competition) === null || _a === void 0 ? void 0 : _a.sport.name) {
            loadTheme(match.competition.sport.name);
        }
    }, [(_a = match === null || match === void 0 ? void 0 : match.competition) === null || _a === void 0 ? void 0 : _a.sport.name]);
    const preloadThemeFonts = (theme) => __awaiter(void 0, void 0, void 0, function* () {
        yield Promise.all(Object.keys(theme.defaultThemeProperties)
            .filter(key => key.slice(-4) === 'Font' &&
            typeof theme.defaultThemeProperties[key] === 'object')
            .map((key) => __awaiter(void 0, void 0, void 0, function* () {
            const { fontFamily, fontWeight } = theme.defaultThemeProperties[key];
            const loader = new FontFaceObserver(fontFamily, {
                weight: fontWeight
            });
            return new Promise((resolve, reject) => {
                loader
                    .load(null, 20000)
                    .then(() => {
                    resolve();
                })
                    .catch(error => {
                    console.error('A critical font is not available:', error);
                    console.error({ fontFamily, fontWeight });
                });
            });
        })));
        setThemeFontsLoaded(true);
    });
    useEffect(() => {
        if (theme) {
            preloadThemeFonts(theme);
        }
    }, [theme]);
    if (!themeFontsLoaded || !overlay || !match) {
        return null;
    }
    const { width, height } = dimensionsFromResolution(overlay.resolution, !!resizable);
    return (React.createElement("div", { style: Object.assign({ position: 'relative' }, (resizable
            ? {
                width: '100%',
                paddingTop: '56.25%'
            }
            : {
                width: width + 'px',
                height: height + 'px'
            })) },
        React.createElement(Background, Object.assign({}, { width, height, overlay, sport: match.competition.sport.name })),
        overlay.showWatermarks && (React.createElement(React.Fragment, null,
            React.createElement(MaxAreaWatermark, Object.assign({}, theme === null || theme === void 0 ? void 0 : theme.watermarks.primary, { src: (_c = (_b = match === null || match === void 0 ? void 0 : match.competition) === null || _b === void 0 ? void 0 : _b.watermark) === null || _c === void 0 ? void 0 : _c.url })),
            React.createElement(MaxAreaWatermark, Object.assign({}, theme === null || theme === void 0 ? void 0 : theme.watermarks.secondary, { src: (_e = (_d = match === null || match === void 0 ? void 0 : match.competition) === null || _d === void 0 ? void 0 : _d.secondaryWatermark) === null || _e === void 0 ? void 0 : _e.url })))),
        [...((theme === null || theme === void 0 ? void 0 : theme.graphics) || []), detachedAd].map(({ component: Component, animations, name, dataSpec, getAnimPath, delayMap, zIndex, adTransformObject, triggerToGraphicData }) => {
            if (filteredGraphics && !filteredGraphics.includes(name)) {
                return null;
            }
            if ((theme === null || theme === void 0 ? void 0 : theme.disabledGraphics) && theme.disabledGraphics.includes(name)) {
                return null;
            }
            return (React.createElement(GraphicWrapper, { zIndex: zIndex, noAnimation: noAnimation, fieldDataSpec: dataSpec, delayMap: delayMap, key: name, triggerToGraphicData: triggerToGraphicData, auto: overlay.autoMode, name: name, template: Component, animationList: animations, adTransformObject: adTransformObject, getAnimPath: getAnimPath }));
        })));
};
const OverlayReadyGuard = ({ resizable, filteredGraphics, noAnimation }) => {
    const { overlay, match } = OverlayContainer.useContainer();
    const { data: { matchSummary } } = DelayedSummary.useContainer();
    const loaded = useMemo(() => {
        return overlay && match && matchSummary;
    }, [overlay, match, matchSummary]);
    return loaded ? (React.createElement(ClockContainer.Provider, null,
        React.createElement(DesiredGfxState.Provider, null,
            React.createElement(Ads.Provider, null,
                overlay.autoMode ? (React.createElement(Automation.Provider, { key: name }, '')) : (React.createElement(Manual.Provider, null, '')),
                React.createElement(OverlayComponent, { noAnimation: noAnimation, filteredGraphics: filteredGraphics, resizable: resizable }))))) : null;
};
export const OverlayWithData = ({ overlay, overlaySettings, resizable, manualState, filteredGraphics, correctClientClock = true, noAnimation }) => overlay ? (React.createElement(OverlayContainer.Provider, { initialState: {
        overlay: Object.assign(Object.assign({}, overlay), overlaySettings),
        manualState: manualState || overlay.manualGraphicState,
        overlaySettings
    } },
    React.createElement(DelayedSummary.Provider, { initialState: Object.assign(Object.assign({}, overlay), overlaySettings) },
        correctClientClock && (React.createElement(DelayedSummaryWithClientSkewCorrection.Provider, null, '')),
        React.createElement(OverlayReadyGuard, Object.assign({}, {
            overlay,
            overlaySettings,
            resizable,
            manualState,
            filteredGraphics,
            correctClientClock,
            noAnimation
        }))))) : null;
export const OverlayWithoutClockCorrection = props => (React.createElement(OverlayWithData, Object.assign({}, props, { correctClientClock: false })));
export const OverlayAuthWrapper = props => {
    const { key } = useParams();
    const { status, loginError } = Auth.useContainer();
    const [fetchOverlay, { data, error: fetchOverlayError, loading }] = useLazyQuery(fetchOverlayQuery, {
        fetchPolicy: 'network-only'
    });
    useEffect(() => {
        if (status === 'loggedIn') {
            fetchOverlay({ variables: { key } });
        }
    }, [status]);
    let error;
    if (!key) {
    }
    else if (loginError || status === 'invalidKey') {
        error = new Error('Invalid overlay key');
    }
    else if (fetchOverlayError) {
        error = new Error("Unknow error occured. Couldn't load overlay");
    }
    else if (!loading && !data) {
        error = new Error('Overlay does not exist');
    }
    if (error) {
        return React.createElement("div", null, error.message);
    }
    return data ? (React.createElement(OverlayHeartbeat.Provider, { initialState: data.overlay.key },
        React.createElement(OverlayWithData, Object.assign({ overlay: data === null || data === void 0 ? void 0 : data.overlay }, props, { manualState: data === null || data === void 0 ? void 0 : data.overlay.manualGraphicState })))) : null;
};
//# sourceMappingURL=Overlay.js.map